/**
 * Captura e persiste atribuição de marketing (UTMs + Meta click IDs).
 * First-party only — sem PII. Cookie no domínio pai quando possível.
 */

const STORAGE_KEY = 'dsc_mkt_attribution';
const COOKIE_NAME = 'dsc_mkt_attr';
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60; // 90 dias (janela típica Meta)

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
const META_KEYS = ['fbclid', 'fbp', 'fbc'];
const ALL_KEYS = [...UTM_KEYS, ...META_KEYS, 'landing_page', 'captured_at'];

function isBrowser() {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function parentCookieDomain() {
    const host = window.location.hostname;
    if (!host || host === 'localhost' || /^\d+\.\d+\.\d+\.\d+$/.test(host)) {
        return null;
    }
    if (host.endsWith('descompliquesuacorrecao.com.br')) {
        return '.descompliquesuacorrecao.com.br';
    }
    return null;
}

function readCookie(name) {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name, value, maxAgeSeconds) {
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
    const domain = parentCookieDomain();
    if (domain) {
        cookie += `; domain=${domain}`;
    }
    if (window.location.protocol === 'https:') {
        cookie += '; Secure';
    }
    document.cookie = cookie;
}

function sanitizeString(value, max = 255) {
    if (value == null) return null;
    const text = String(value).trim();
    if (!text) return null;
    return text.slice(0, max);
}

function readMetaBrowserIds() {
    return {
        fbp: sanitizeString(readCookie('_fbp'), 128),
        fbc: sanitizeString(readCookie('_fbc'), 256),
    };
}

function buildFbcFromFbclid(fbclid) {
    if (!fbclid) return null;
    const existing = sanitizeString(readCookie('_fbc'), 256);
    if (existing) return existing;
    return `fb.1.${Date.now()}.${fbclid}`;
}

function parseStored(raw) {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
        return {};
    }
}

function loadStoredAttribution() {
    if (!isBrowser()) return {};

    let fromStorage = {};
    try {
        fromStorage = parseStored(localStorage.getItem(STORAGE_KEY));
    } catch {
        fromStorage = {};
    }

    const fromCookie = parseStored(readCookie(COOKIE_NAME));
    return { ...fromCookie, ...fromStorage };
}

function persistAttribution(data) {
    if (!isBrowser()) return;

    const cleaned = {};
    ALL_KEYS.forEach((key) => {
        const value = sanitizeString(data[key], key === 'landing_page' ? 500 : 255);
        if (value) cleaned[key] = value;
    });

    if (!cleaned.captured_at) {
        cleaned.captured_at = new Date().toISOString();
    }

    const serialized = JSON.stringify(cleaned);
    try {
        localStorage.setItem(STORAGE_KEY, serialized);
    } catch {
        // ignore quota / private mode
    }
    writeCookie(COOKIE_NAME, serialized, COOKIE_MAX_AGE_SECONDS);
    return cleaned;
}

function readQueryAttribution() {
    if (!isBrowser()) return {};

    const params = new URLSearchParams(window.location.search);
    const fromQuery = {};

    UTM_KEYS.forEach((key) => {
        const value = sanitizeString(params.get(key));
        if (value) fromQuery[key] = value;
    });

    const fbclid = sanitizeString(params.get('fbclid'), 255);
    if (fbclid) {
        fromQuery.fbclid = fbclid;
        const fbc = buildFbcFromFbclid(fbclid);
        if (fbc) fromQuery.fbc = fbc;
    }

    return fromQuery;
}

/**
 * Lê query + cookies Meta, faz merge com o já persistido (first-touch nos UTMs;
 * fbp/fbc sempre atualizados quando disponíveis).
 */
export function captureMarketingAttribution() {
    if (!isBrowser()) return {};

    const previous = loadStoredAttribution();
    const fromQuery = readQueryAttribution();
    const metaIds = readMetaBrowserIds();

    const next = { ...previous };

    // First-touch: só preenche UTM/fbclid se ainda não existirem
    [...UTM_KEYS, 'fbclid'].forEach((key) => {
        if (!next[key] && fromQuery[key]) {
            next[key] = fromQuery[key];
        }
    });

    // fbc: query (com fbclid) tem prioridade se ainda não houver; cookie Meta atualiza
    if (!next.fbc && fromQuery.fbc) {
        next.fbc = fromQuery.fbc;
    }
    if (metaIds.fbc) {
        next.fbc = metaIds.fbc;
    }
    if (metaIds.fbp) {
        next.fbp = metaIds.fbp;
    }

    if (!next.landing_page && (fromQuery.utm_source || fromQuery.fbclid)) {
        next.landing_page = sanitizeString(
            `${window.location.pathname}${window.location.search}`,
            500,
        );
    }

    if (!next.captured_at && (fromQuery.utm_source || fromQuery.fbclid || metaIds.fbp)) {
        next.captured_at = new Date().toISOString();
    }

    const hasSignal = ALL_KEYS.some((key) => key !== 'captured_at' && next[key]);
    if (!hasSignal) {
        return {};
    }

    return persistAttribution(next) || next;
}

/**
 * Snapshot atual para enviar no POST da compra (sem campos vazios).
 */
export function getMarketingAttributionPayload() {
    const current = captureMarketingAttribution();
    const payload = {};

    [...UTM_KEYS, ...META_KEYS].forEach((key) => {
        if (current[key]) payload[key] = current[key];
    });

    if (current.landing_page) payload.landing_page = current.landing_page;
    if (current.captured_at) payload.captured_at = current.captured_at;

    return payload;
}

export function initMarketingAttribution() {
    if (!isBrowser()) return;
    captureMarketingAttribution();

    // Pixel pode setar _fbp/_fbc um pouco depois do load
    window.setTimeout(() => {
        captureMarketingAttribution();
    }, 1500);
}
